import { useMutation } from "@tanstack/react-query";

import { useGraphQLClient } from "../graphql/client";
import {
  customerAccessTokenCreateMutation,
  customerActivateMutation,
  customerAddressCreateMutation,
  customerAddressDeleteMutation,
  customerAddressUpdateMutation,
  customerCreateMutation,
  customerDefaultAddressUpdateMutation,
  customerQuery,
  customerRecoverMutation,
  customerResetMutation,
  customerUpdateMutation,
  totalPaidAmountQuery,
} from "../graphql/customer-gql";
import { useCacheFirstRequest } from "../graphql/queryClient";
import type {
  ICustomerAccessTokenCreateMutation,
  ICustomerActivateMutation,
  ICustomerAddressCreateMutation,
  ICustomerAddressDeleteMutation,
  ICustomerAddressUpdateMutation,
  ICustomerCreateMutation,
  ICustomerDefaultAddressUpdateMutation,
  ICustomerOrdersQuery,
  ICustomerQuery,
  ICustomerRecoverMutation,
  ICustomerResetMutation,
  ICustomerUpdateMutation,
} from "../interface/customer";
import { locale } from "../locales";
import { ToastType } from "../types/layout";
import type { IUseCustomerQueryParams } from "../types/queries";
import type { CustomerErrorCode, Maybe } from "../types/storefront.types";
import { customerErrorHandler } from "../utils/errorHandler";

export default function useCustomerQuery({ lang, accessToken, update, onSessionExpired, openToast }: IUseCustomerQueryParams) {
  const request = useGraphQLClient();
  const cacheFirstRequest = useCacheFirstRequest();

  const notifyUserErrors = (userErrors: { code?: Maybe<CustomerErrorCode> }[]) => {
    userErrors.forEach((error) => {
      openToast({ type: ToastType.Error, message: customerErrorHandler(error.code, lang) });
    });
  };

  // 회원 정보 조회 (no-cache)
  const fetchCustomerMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerQuery>(customerQuery, variables),
    onSuccess: (data) => {
      if (!data.customer) {
        onSessionExpired?.();
        return;
      }

      update(data.customer);
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 로그인
  const createTokenMutation = useMutation({
    mutationFn: (variables: Record<string, any>) =>
      request<ICustomerAccessTokenCreateMutation>(customerAccessTokenCreateMutation, variables),
    onSuccess: (data) => {
      if (data.customerAccessTokenCreate.customerUserErrors.length) {
        notifyUserErrors(data.customerAccessTokenCreate.customerUserErrors);
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 회원가입
  const createCustomerMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerCreateMutation>(customerCreateMutation, variables),
    onSuccess: (data) => {
      if (data.customerCreate.customerUserErrors.length) {
        notifyUserErrors(data.customerCreate.customerUserErrors);
        return;
      }

      openToast({ message: locale(lang).success.register_complete, type: ToastType.Success });
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 비밀번호 찾기 (재설정 메일 발송)
  const resetPasswordMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerRecoverMutation>(customerRecoverMutation, variables),
    onSuccess: ({ customerRecover }) => {
      if (customerRecover.customerUserErrors.length) {
        notifyUserErrors(customerRecover.customerUserErrors);
        return;
      }

      openToast({ message: locale(lang).success.password_reset_email, type: ToastType.Success });
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 비밀번호 재설정 (메일 링크의 id/resetToken 으로 새 비밀번호 설정)
  const resetByTokenMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerResetMutation>(customerResetMutation, variables),
    onSuccess: ({ customerReset }) => {
      if (customerReset.customerUserErrors.length) notifyUserErrors(customerReset.customerUserErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 계정 활성화 (초대 메일 링크의 id/activationToken 으로 비밀번호 설정)
  const activateMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerActivateMutation>(customerActivateMutation, variables),
    onSuccess: ({ customerActivate }) => {
      if (customerActivate.customerUserErrors.length) notifyUserErrors(customerActivate.customerUserErrors);
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 회원 정보 업데이트
  const updateCustomerMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerUpdateMutation>(customerUpdateMutation, variables),
    onSuccess: ({ customerUpdate }) => {
      if (customerUpdate.customerUserErrors.length) {
        notifyUserErrors(customerUpdate.customerUserErrors);
        return;
      }

      update(customerUpdate.customer);
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 비밀번호 변경 (no-cache)
  const changePasswordMutation = useMutation({
    mutationFn: (variables: Record<string, any>) => request<ICustomerUpdateMutation>(customerUpdateMutation, variables),
    onSuccess: ({ customerUpdate }) => {
      if (customerUpdate.customerUserErrors.length) {
        notifyUserErrors(customerUpdate.customerUserErrors);
        return;
      }

      if (customerUpdate.customerAccessToken?.accessToken) {
        openToast({ message: locale(lang).success.password_change, type: ToastType.Success });
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 기본 배송지 설정 (customerAccessToken 기본 주입)
  const setDefaultAddressMutation = useMutation({
    mutationFn: (variables: Record<string, any>) =>
      request<ICustomerDefaultAddressUpdateMutation>(customerDefaultAddressUpdateMutation, {
        customerAccessToken: accessToken,
        ...variables,
      }),
    onSuccess: (data) => {
      if (data.customerDefaultAddressUpdate.customerUserErrors.length) {
        notifyUserErrors(data.customerDefaultAddressUpdate.customerUserErrors);
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 배송지 추가
  const createAddressMutation = useMutation({
    mutationFn: (variables: Record<string, any>) =>
      request<ICustomerAddressCreateMutation>(customerAddressCreateMutation, { customerAccessToken: accessToken, ...variables }),
    onSuccess: (data) => {
      if (data.customerAddressCreate.customerUserErrors.length) {
        notifyUserErrors(data.customerAddressCreate.customerUserErrors);
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 배송지 수정
  const editAddressMutation = useMutation({
    mutationFn: (variables: Record<string, any>) =>
      request<ICustomerAddressUpdateMutation>(customerAddressUpdateMutation, { customerAccessToken: accessToken, ...variables }),
    onSuccess: (data) => {
      if (data.customerAddressUpdate.customerUserErrors.length) {
        notifyUserErrors(data.customerAddressUpdate.customerUserErrors);
      }
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  // 배송지 삭제
  const deleteAddressMutation = useMutation({
    mutationFn: (variables: Record<string, any>) =>
      request<ICustomerAddressDeleteMutation>(customerAddressDeleteMutation, { customerAccessToken: accessToken, ...variables }),
    onSuccess: (data) => {
      if (data.customerAddressDelete.customerUserErrors.length) {
        notifyUserErrors(data.customerAddressDelete.customerUserErrors);
        return;
      }

      void fetchCustomerMutation.mutateAsync({ customerAccessToken: accessToken });
    },
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 총 결제 금액(주문 금액 합산) 조회 */
  const totalPaidAmountMutation = useMutation({
    mutationFn: (variables: Record<string, any>) =>
      cacheFirstRequest<ICustomerOrdersQuery>("customer-total-paid", totalPaidAmountQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  return {
    createToken: createTokenMutation.mutateAsync,
    createCustomer: createCustomerMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    resetPasswordByToken: resetByTokenMutation.mutateAsync,
    resetPasswordByTokenLoading: resetByTokenMutation.isPending,
    activateCustomer: activateMutation.mutateAsync,
    activateCustomerLoading: activateMutation.isPending,
    fetchCustomer: fetchCustomerMutation.mutateAsync,
    updateCustomer: updateCustomerMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    setDefaultAddress: setDefaultAddressMutation.mutateAsync,
    createAddress: createAddressMutation.mutateAsync,
    editAddress: editAddressMutation.mutateAsync,
    deleteAddress: deleteAddressMutation.mutateAsync,
    fetchCustomerLoading: fetchCustomerMutation.isPending,
    updateCustomerLoading: updateCustomerMutation.isPending,
    changePasswordLoading: changePasswordMutation.isPending,
    createTokenLoading: createTokenMutation.isPending,
    createCustomerLoading: createCustomerMutation.isPending,
    resetPasswordLoading: resetPasswordMutation.isPending,
    setDefaultAddressLoading: setDefaultAddressMutation.isPending,
    createAddressLoading: createAddressMutation.isPending,
    editAddressLoading: editAddressMutation.isPending,
    deleteAddressLoading: deleteAddressMutation.isPending,
    fetchTotalPaidAmount: totalPaidAmountMutation.mutateAsync,
    totalPaidAmountData: totalPaidAmountMutation.data,
    totalPaidAmountLoading: totalPaidAmountMutation.isPending,
  };
}
